import { useEffect, useRef } from 'react';
import { DIAGNOSTIC_FACTS } from '@/lib/marketingCtas';
import { revealStyle, useScrollReveal } from '@/hooks/useScrollReveal';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

/**
 * Facts Band (spec 010 §4 F): structural facts the code makes true, nothing else.
 *
 * - 8 areas: `SatDomain` taxonomy (`DOMAINS_BY_SECTION` in api/sat.ts), 4 reading and writing + 4 math.
 * - 56 skills: `select count(*) from sat_skills` (8 distinct domains).
 *   VERIFIED 2026-09-30 against the local stack's Postgres (seeded by V071/V072).
 * - 24-question diagnostic: `DIAGNOSTIC_FACTS` (one constant, lib/marketingCtas.ts).
 * - Full-length Bluebook baseline: the course asks for a full practice test in College Board's Bluebook.
 *
 * No question-bank count on purpose: it is unverified against prod, so it is left out. No outcome
 * numbers either; the course has no results to quote.
 */
export const SAT_SKILL_COUNT = 56;
export const SAT_AREA_COUNT = 8;

interface Fact {
  /** A number counts up on reveal; text is shown as is. */
  value: number | string;
  label: string;
}

export const SAT_FACTS: readonly Fact[] = [
  { value: SAT_AREA_COUNT, label: 'SAT areas, each mapped from your diagnostic' },
  { value: SAT_SKILL_COUNT, label: 'skills inside them, so practice can aim precisely' },
  { value: DIAGNOSTIC_FACTS.questions, label: `questions in the diagnostic, ${DIAGNOSTIC_FACTS.minutesLabel}` },
  { value: 'Full-length', label: 'Bluebook practice test sets your baseline score' },
];

const COUNT_UP_MS = 700; // spec 010 §6: count-ups never exceed 800ms

function CountUp({ value, active }: { value: number; active: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || !active || reduced) return;
    // The final number is in the HTML (prerender, no-JS, tests); animate a copy only once revealed.
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / COUNT_UP_MS);
      el.textContent = String(Math.round(value * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    el.textContent = '0';
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      el.textContent = String(value);
    };
  }, [active, reduced, value]);
  return (
    <span ref={ref} className="tabular-nums" data-testid="fact-number">
      {value}
    </span>
  );
}

export function SatStatsBand() {
  const { ref, isVisible } = useScrollReveal(0.2);
  return (
    <section aria-label="The course in numbers" className="w-full bg-primary py-14 text-primary-foreground md:py-20">
      <div ref={ref} style={revealStyle(isVisible)} className="mx-auto max-w-6xl px-4 sm:px-6">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-10 lg:grid-cols-4 lg:gap-x-0 lg:divide-x lg:divide-primary-foreground/20" data-testid="facts-band">
          {SAT_FACTS.map((f) => (
            <div key={f.label} className="flex flex-col gap-3 lg:px-8 lg:first:pl-0 lg:last:pr-0">
              <dt className="flex h-14 items-end text-5xl font-bold leading-none tracking-tight sm:h-16 sm:text-6xl">
                {typeof f.value === 'number' ? (
                  <CountUp value={f.value} active={isVisible} />
                ) : (
                  <span className="text-3xl sm:text-4xl">{f.value}</span>
                )}
              </dt>
              <dd className="max-w-[22ch] text-[15px] font-medium leading-snug text-primary-foreground">{f.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
